import { supabase } from '../../supabase';
import { stampCreatedAt, stampTimestamps } from '../../utils/dbTimestamps';
import { ensureGymId, setActiveGymId, getCachedGymId } from '../receptionist/gymContext';
import {
  fetchPayments,
  fetchTrainers,
  fetchGuestPasses,
} from '../receptionist/receptionistApi';
import { fetchBranchNamesLite } from '../gym/catalogApi';
import { fetchFinanceLedgerAllRpc } from '../shared/financeLedgerRpcApi';
import { rowToPayment, rowToGuestPass, rowToTrainer } from '../receptionist/mappers';
import { dedupePeople, dedupeGuestPasses } from '../../utils/dedupeList';
import { saleRowLeft } from '../../pages/receptionist/sales/salePaymentUtils';
import { cachedQuery, invalidateCache } from '../cache/queryCache';
import { runGenericMutation } from '../offline/runMutation';
import {
  CHART_OF_ACCOUNTS,
  EXPENSE_CATEGORIES,
  buildFinanceLedgerBundle,
  getChartOfAccountsForGym,
  addCustomChartAccount,
  updateCustomChartAccount,
  deleteCustomChartAccount,
  suggestNextAccountCode,
  ACCOUNT_TYPES,
  COA_PARENT_ACCOUNTS,
  COA_CASH_BANK_EXPENSE_PARENTS,
  COA_Z_REPORT_LOAN_PARENTS,
  COA_CIH_CHILDREN,
  COA_ORCV_CHILDREN,
  COA_PEARL_CHILDREN,
  COA_TENDER_CODE_ALIASES,
  normalizeCoaAccountLabel,
  loadCoaChildAccounts,
  saveCoaChildAccounts,
  localDateKey,
  calendarDateKey,
} from '../../pages/admin/finance/financeData';
import {
  buildBankAccountsSavePayload,
  computeZBankClosings,
  sumZBankField,
} from '../../pages/shared/zReport1Data';

async function resolveGymId(explicitGymId) {
  const gid = explicitGymId || getCachedGymId();
  if (gid) {
    setActiveGymId(gid);
    return gid;
  }
  return ensureGymId();
}

function throwIfExpensesMissing(error) {
  if (error && /expenses.*schema cache|relation.*expenses.*does not exist/i.test(error.message || '')) {
    throw new Error(
      'Expenses table not found. Open Supabase → SQL Editor, run supabase/fix_finance_expenses_persist.sql, then refresh.',
    );
  }
}

function resolveExpenseDisplayCode(row = {}) {
  const fromExpenseCode = String(row.expense_code || row.expenseCode || row.code || '').trim();
  if (fromExpenseCode) return fromExpenseCode;
  const accountCode = String(row.account_code || row.accountCode || '').trim();
  // CoA expense codes are stored as E1000+ (also dual-written to account_code on older schemas)
  if (/^E\d+/i.test(accountCode)) return accountCode.toUpperCase();
  return '';
}

function normalizeExpensePaymentMethod(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'bank' || raw === 'card' || raw === 'qr' || raw === 'online' || raw === 'transfer') {
    return 'bank';
  }
  return 'cash';
}

function rowToExpense(row) {
  const title = String(row.account_title || '').trim();
  const description = String(row.description || '').trim();
  const code = resolveExpenseDisplayCode(row);
  const method = normalizeExpensePaymentMethod(row.payment_method || row.paymentMethod || row.method);
  return {
    id: row.id,
    gymId: row.gym_id || '',
    accountTitle: title || description || 'Expense',
    description: title ? description : '',
    expenseCode: code,
    code,
    category: row.category || 'Other',
    branch: String(row.branch_name || row.branch || '').trim(),
    amount: Number(row.amount) || 0,
    date: row.expense_date || '',
    status: row.status || 'paid',
    accountCode: row.account_code || '5000',
    paymentMethod: method,
    method,
  };
}

/** Keep only rows that belong to this gym. */
function scopeToGym(rows, gymId) {
  if (!gymId) return [];
  return (rows || []).filter((row) => {
    const rowGym = row.gymId || row.gym_id;
    return rowGym && String(rowGym) === String(gymId);
  });
}

export async function fetchExpenses(explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) return [];

  let query = supabase
    .from('expenses')
    .select('*')
    .eq('gym_id', gid);

  // Prefer newest first; fall back if expense_date column is missing on older schemas
  let { data, error } = await query.order('expense_date', { ascending: false });

  if (error && /expense_date|column/i.test(error.message)) {
    ({ data, error } = await supabase.from('expenses').select('*').eq('gym_id', gid));
  }

  if (error) {
    throwIfExpensesMissing(error);
    throw error;
  }

  return scopeToGym((data || []).map(rowToExpense), gid);
}

/** One calendar day of expenses — used by Daily Z (avoids downloading the full table). */
export async function fetchExpensesForDate(date, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(date || '').slice(0, 10);
  if (!gid || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return [];

  let { data, error } = await supabase
    .from('expenses')
    .select('*')
    .eq('gym_id', gid)
    .eq('expense_date', day);

  if (error && /expense_date|column/i.test(error.message || '')) {
    const all = await fetchExpenses(gid).catch(() => []);
    return (all || []).filter((row) => String(row.date || '').slice(0, 10) === day);
  }
  if (error) {
    throwIfExpensesMissing(error);
    throw error;
  }
  return scopeToGym((data || []).map(rowToExpense), gid);
}

/** Inclusive expense_date window for Z sale-range / weekly totals. */
export async function fetchExpensesForDateRange(dateFrom, dateTo, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const from = String(dateFrom || '').slice(0, 10);
  const to = String(dateTo || '').slice(0, 10);
  if (!gid || !/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return [];
  const start = from <= to ? from : to;
  const end = from <= to ? to : from;

  let { data, error } = await supabase
    .from('expenses')
    .select('*')
    .eq('gym_id', gid)
    .gte('expense_date', start)
    .lte('expense_date', end);

  if (error && /expense_date|column/i.test(error.message || '')) {
    const all = await fetchExpenses(gid).catch(() => []);
    return (all || []).filter((row) => {
      const d = String(row.date || '').slice(0, 10);
      return d >= start && d <= end;
    });
  }
  if (error) {
    throwIfExpensesMissing(error);
    throw error;
  }
  return scopeToGym((data || []).map(rowToExpense), gid);
}

async function insertExpenseRow(row) {
  return runGenericMutation({
    table: 'expenses',
    op: 'insert',
    payload: stampCreatedAt(row),
    source: 'finance',
  });
}

export async function createExpense(form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) {
    throw new Error('No gym is linked to this account. Sign out and sign in again.');
  }

  const accountTitle = String(form.accountTitle || form.title || '').trim();
  const note = String(form.description || '').trim();
  const category = String(form.category || 'Other').trim() || 'Other';
  const expenseCode = String(form.expenseCode || form.code || '').trim();
  const branchName = String(form.branch || form.branchName || '').trim();
  const categoryAccountCode = (
    /salary|salaries|payroll/i.test(category) ? '5100'
      : /rent|utilit/i.test(category) ? '5200'
        : /equip|maint/i.test(category) ? '5300'
          : /market/i.test(category) ? '5400'
            : '5000'
  );

  // Keep category registry in sync whenever an expense is saved
  try {
    await ensureExpenseCategoryRemote({
      name: category,
      codeBase: Number(form.categoryCodeBase) || undefined,
    }, gid);
  } catch {
    // Category sync is best-effort; expense row still stores category text
  }

  const paymentMethod = normalizeExpensePaymentMethod(
    form.paymentMethod || form.method || 'cash',
  );

  const row = {
    gym_id: gid,
    branch_name: branchName || null,
    category,
    account_title: accountTitle || null,
    expense_code: expenseCode || null,
    description: accountTitle
      ? note
      : (note || accountTitle || 'Expense'),
    amount: Number(form.amount) || 0,
    expense_date: form.date || new Date().toISOString().slice(0, 10),
    status: form.status || 'paid',
    // Dual-write E-codes into account_code so Code still persists if expense_code column is missing
    account_code: form.accountCode || expenseCode || categoryAccountCode,
    payment_method: paymentMethod,
  };

  let { data, error } = await insertExpenseRow(row);

  // Retry without optional columns that may be missing on older schemas
  const optionalCols = ['payment_method', 'account_code', 'branch_name', 'account_title', 'expense_code'];
  for (let i = 0; i < 8 && error; i += 1) {
    const missing = optionalCols.find((col) => new RegExp(col, 'i').test(error.message || ''));
    if (!missing || !Object.prototype.hasOwnProperty.call(row, missing)) break;
    if (missing === 'account_title' && accountTitle) {
      // Preserve title in description when column is unavailable
      row.description = note ? `${accountTitle} — ${note}` : accountTitle;
    }
    if (missing === 'expense_code' && expenseCode) {
      row.account_code = expenseCode;
    }
    delete row[missing];
    ({ data, error } = await insertExpenseRow(row));
  }

  if (error) {
    throwIfExpensesMissing(error);
    throw error;
  }

  invalidateCache(`gym:${gid}:`);
  return rowToExpense(data);
}

export async function deleteExpense(id, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const { error } = await supabase
    .from('expenses')
    .delete()
    .eq('id', id)
    .eq('gym_id', gid);
  if (error) {
    throwIfExpensesMissing(error);
    throw error;
  }
  invalidateCache(`gym:${gid}:`);
}

function rowToExpenseCategory(row) {
  return {
    id: row.id,
    name: String(row.name || '').trim(),
    codeBase: Number(row.code_base) || 1000,
  };
}

export async function fetchExpenseCategories(explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) return [];

  const { data, error } = await supabase
    .from('expense_categories')
    .select('*')
    .eq('gym_id', gid)
    .order('code_base', { ascending: true });

  if (error) {
    if (/expense_categories|schema cache|does not exist/i.test(error.message || '')) {
      return [];
    }
    throw error;
  }

  return (data || [])
    .map(rowToExpenseCategory)
    .filter((row) => row.name);
}

export async function createExpenseCategoryRemote(form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) {
    throw new Error('No gym is linked to this account. Sign out and sign in again.');
  }
  const name = String(form.name || '').trim();
  if (!name) throw new Error('Category name is required.');
  const codeBase = Number(form.codeBase) || 1000;

  const { data, error } = await supabase
    .from('expense_categories')
    .insert(stampCreatedAt({
      gym_id: gid,
      name,
      code_base: codeBase,
    }))
    .select('*')
    .single();

  if (error) {
    if (/expense_categories|schema cache|does not exist/i.test(error.message || '')) {
      throw new Error(
        'Expense categories table not found. Run supabase/fix_finance_expenses_persist.sql in Supabase, then retry.',
      );
    }
    if (/duplicate|unique/i.test(error.message || '')) {
      throw new Error(`Category "${name}" already exists.`);
    }
    throw error;
  }

  invalidateCache(`gym:${gid}:`);
  return rowToExpenseCategory(data);
}

/** Create category in Supabase if missing (used when saving an expense). */
export async function ensureExpenseCategoryRemote(form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const name = String(form?.name || form?.category || '').trim();
  if (!gid || !name) return null;

  const existing = await fetchExpenseCategories(gid);
  const found = existing.find((row) => row.name.toLowerCase() === name.toLowerCase());
  if (found) return found;

  const used = new Set(existing.map((row) => Number(row.codeBase) || 0));
  let codeBase = Number(form?.codeBase) || 1000;
  if (!Number.isFinite(codeBase) || codeBase < 1000) codeBase = 1000;
  while (used.has(codeBase) && codeBase <= 9000) codeBase += 1000;

  try {
    return await createExpenseCategoryRemote({ name, codeBase }, gid);
  } catch (err) {
    if (/already exists/i.test(err.message || '')) {
      const again = await fetchExpenseCategories(gid);
      return again.find((row) => row.name.toLowerCase() === name.toLowerCase()) || { name, codeBase };
    }
    // Don't block expense save if categories table is missing
    if (/table not found|does not exist|schema cache/i.test(err.message || '')) {
      return null;
    }
    throw err;
  }
}

export async function deleteExpenseCategoryRemote(name, explicitGymId, { deleteExpenses = true } = {}) {
  const gid = await resolveGymId(explicitGymId);
  const category = String(name || '').trim();
  if (!category) throw new Error('Category name is required.');

  if (deleteExpenses) {
    const { error: expenseError } = await supabase
      .from('expenses')
      .delete()
      .eq('gym_id', gid)
      .ilike('category', category);
    if (expenseError && !/schema cache|does not exist/i.test(expenseError.message || '')) {
      throw expenseError;
    }
  }

  const { error } = await supabase
    .from('expense_categories')
    .delete()
    .eq('gym_id', gid)
    .ilike('name', category);

  if (error) {
    if (/expense_categories|schema cache|does not exist/i.test(error.message || '')) {
      throw new Error(
        'Expense categories table not found. Run supabase/fix_finance_expenses_persist.sql in Supabase, then retry.',
      );
    }
    throw error;
  }

  invalidateCache(`gym:${gid}:`);
}

/**
 * Load finance sources for the logged-in admin's gym only.
 */
function mapFinanceLedgerPaymentRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
    trainer: members.trainer,
  });
  const left = Number(row.pending_amount);
  return {
    ...payment,
    trainer: members.trainer || row.trainer || payment.trainer || '',
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
    saleDay: row.sale_day ? String(row.sale_day).slice(0, 10) : (payment.saleDay || ''),
    pendingAmount: Number.isFinite(left) ? left : saleRowLeft(payment),
  };
}

function assembleFinanceLedgerBundle({
  gymId,
  payments = [],
  expenses = [],
  trainers = [],
  guestPasses = [],
  branchNames = [],
}) {
  const memberById = new Map();
  (payments || []).forEach((p) => {
    if (!p.memberId || memberById.has(p.memberId)) return;
    memberById.set(p.memberId, {
      id: p.memberId,
      name: p.member || '—',
      gymId: p.gymId || '—',
      branch: p.branch || '',
    });
  });
  const members = [...memberById.values()];
  const allowedBranchNames = new Set((branchNames || []).filter(Boolean));
  const branches = [...allowedBranchNames].map((name) => ({ name }));
  const safeExpenses = scopeToGym(expenses, gymId);
  const bundle = buildFinanceLedgerBundle({
    members,
    memberships: [],
    payments,
    trainers,
    guestPasses,
    expenses: safeExpenses,
    branches,
    chartOfAccounts: getChartOfAccountsForGym(gymId),
  });
  return {
    gymId,
    chartOfAccounts: getChartOfAccountsForGym(gymId),
    expenseCategories: EXPENSE_CATEGORIES,
    ...bundle,
    branchOptions: [...allowedBranchNames].sort(),
    expenses: bundle.expenses || [],
  };
}

export async function fetchFinanceLedgerData(explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) {
    throw new Error('No gym is linked to this account. Sign out and sign in again.');
  }

  setActiveGymId(gid);

  return cachedQuery(`gym:${gid}:finance-ledger:v5`, async () => {
    try {
      const rpc = await fetchFinanceLedgerAllRpc(gid);
      if (rpc) {
        const payments = (rpc.payments || []).map(mapFinanceLedgerPaymentRow);
        const expenses = (rpc.expenses || []).map(rowToExpense);
        const trainers = dedupePeople((rpc.trainers || []).map(rowToTrainer));
        const guestPasses = dedupeGuestPasses((rpc.guestPasses || []).map((row) => ({
          ...rowToGuestPass(row),
          branch: row.branch_name || row.branch || '',
        })));
        return assembleFinanceLedgerBundle({
          gymId: gid,
          payments,
          expenses,
          trainers,
          guestPasses,
          branchNames: rpc.branches,
        });
      }
    } catch (err) {
      console.warn('Finance ledger RPC failed, using fallback', err?.message || err);
    }

    const [
      expenseResult,
      payments,
      trainers,
      guestPasses,
      branchNames,
    ] = await Promise.all([
      fetchExpenses(gid).then(
        (rows) => ({ rows, error: null }),
        (err) => ({ rows: [], error: err }),
      ),
      fetchPayments().catch(() => []),
      fetchTrainers().catch(() => []),
      fetchGuestPasses().catch(() => []),
      fetchBranchNamesLite().catch(() => []),
    ]);

    if (expenseResult.error) {
      throwIfExpensesMissing(expenseResult.error);
      throw expenseResult.error;
    }

    return assembleFinanceLedgerBundle({
      gymId: gid,
      payments,
      expenses: expenseResult.rows,
      trainers,
      guestPasses,
      branchNames,
    });
  }, 60_000);
}

export async function fetchMemberLedgerByGymId(gymMemberId, explicitGymId) {
  const query = String(gymMemberId || '').trim();
  if (!query) return null;

  const data = await fetchFinanceLedgerData(explicitGymId);
  const normalized = query.toLowerCase();

  const member = data.members.find(
    (m) => String(m.gymId || '').toLowerCase() === normalized
      || String(m.gymId || '').toLowerCase().includes(normalized)
      || String(m.name || '').toLowerCase().includes(normalized),
  );

  if (!member) return null;

  return data.memberLedgers.find((ledger) => ledger.memberId === member.id) || null;
}

export async function createChartAccount(form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  const row = addCustomChartAccount(gid, form);
  invalidateCache(`gym:${gid}:`);
  return row;
}

export async function updateChartAccount(code, form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  const row = updateCustomChartAccount(gid, code, form);
  invalidateCache(`gym:${gid}:`);
  return row;
}

export async function removeChartAccount(code, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  deleteCustomChartAccount(gid, code);
  invalidateCache(`gym:${gid}:`);
  return true;
}

function throwIfCoaMissing(error) {
  if (error && /coa_parent_accounts|coa_child_accounts|coa_ledger_entries|schema cache|does not exist/i.test(error.message || '')) {
    throw new Error(
      'Chart of Accounts tables not found. Open Supabase → SQL Editor, run supabase/fix_coa_accounts.sql, then refresh.',
    );
  }
}

function rowToCoaParent(row) {
  return {
    id: row.id,
    code: String(row.code || '').trim().toUpperCase(),
    name: String(row.name || '').trim(),
    type: String(row.account_type || row.type || 'asset').trim().toLowerCase(),
    sortOrder: Number(row.sort_order) || 0,
  };
}

function rowToCoaChild(row, entries = []) {
  return {
    id: row.id,
    code: String(row.code || '').trim().toUpperCase(),
    parentCode: String(row.parent_code || row.parentCode || '').trim().toUpperCase(),
    name: String(row.name || '').trim(),
    description: String(row.description || '').trim(),
    createdAt: String(row.created_at || '').slice(0, 10),
    entries,
  };
}

/** Infer cash vs bank from voucher ref (CPV/CRV → cash, BPV/BRV → bank). */
function inferTenderFromLedgerRef(ref) {
  const r = String(ref || '').trim().toUpperCase();
  if (!r || r === '—') return null;
  // Bank payment / received vouchers
  if (/^BPV\b/.test(r) || r.startsWith('BPV-') || r.startsWith('BPV_') || /^BPV\d/.test(r)) {
    return 'bank';
  }
  if (/^BRV\b/.test(r) || r.startsWith('BRV-') || r.startsWith('BRV_') || /^BRV\d/.test(r)) {
    return 'bank';
  }
  // Cash payment / received vouchers
  if (/^CPV\b/.test(r) || r.startsWith('CPV-') || r.startsWith('CPV_') || /^CPV\d/.test(r)) {
    return 'cash';
  }
  if (/^CRV\b/.test(r) || r.startsWith('CRV-') || r.startsWith('CRV_') || /^CRV\d/.test(r)) {
    return 'cash';
  }
  if (/\bBANK\b/.test(r)) return 'bank';
  if (/\bCASH\b/.test(r)) return 'cash';
  return null;
}

function isCashPaymentVoucherRef(ref) {
  const r = String(ref || '').trim().toUpperCase();
  return /^CPV\b/.test(r) || r.startsWith('CPV-') || r.startsWith('CPV_') || /^CPV\d/.test(r);
}

function isCashReceiptVoucherRef(ref) {
  const r = String(ref || '').trim().toUpperCase();
  return /^CRV\b/.test(r) || r.startsWith('CRV-') || r.startsWith('CRV_') || /^CRV\d/.test(r);
}

function isJournalVoucherRef(ref) {
  const r = String(ref || '').trim().toUpperCase();
  if (!r || r === '—') return false;
  return /^JV\b/.test(r) || r.startsWith('JV-') || r.startsWith('JV_') || /^JV\d/.test(r);
}

function isCashVoucherRef(ref) {
  return isCashPaymentVoucherRef(ref) || isCashReceiptVoucherRef(ref);
}

/** Cash in Hand child codes — the single cash-book effect for CPV/CRV. */
function isCashInHandCode(code) {
  const c = String(code || '').trim().toUpperCase();
  if (!c) return false;
  return c === 'CIH' || c.startsWith('CIH-') || c === 'CIH-CASH';
}

function isBankVoucherRef(ref) {
  const r = String(ref || '').trim().toUpperCase();
  if (!r || r === '—') return false;
  return /^BPV\b/.test(r) || r.startsWith('BPV-') || r.startsWith('BPV_') || /^BPV\d/.test(r)
    || /^BRV\b/.test(r) || r.startsWith('BRV-') || r.startsWith('BRV_') || /^BRV\d/.test(r);
}

function isCashAtBankCode(code, parentCode = '') {
  const c = String(code || '').trim().toUpperCase();
  const p = String(parentCode || '').trim().toUpperCase();
  if (!c && !p) return false;
  return p === 'CAB' || c === 'CAB' || c.startsWith('CAB-') || c === 'PAY-BANK';
}

function resolveLedgerTender(row) {
  const rawMethod = row?.payment_method ?? row?.paymentMethod ?? row?.method;
  if (rawMethod != null && String(rawMethod).trim() !== '') {
    return normalizeExpensePaymentMethod(rawMethod);
  }
  return inferTenderFromLedgerRef(row?.ref || row?.reference);
}

function rowToCoaLedgerEntry(row, childMeta = {}, voucherDateByRef = null) {
  const ref = String(row.ref || '').trim();
  const journal = isJournalVoucherRef(ref);
  const method = journal ? null : resolveLedgerTender(row);
  const refKey = ref.toUpperCase();
  const fromVoucher = voucherDateByRef instanceof Map
    ? (voucherDateByRef.get(refKey) || voucherDateByRef.get(ref) || '')
    : '';
  return {
    id: row.id,
    date: fromVoucher || calendarDateKey(row.entry_date || row.date),
    description: String(row.description || '').trim() || '—',
    debit: Number(row.debit) || 0,
    credit: Number(row.credit) || 0,
    ref: ref || '—',
    childCode: String(row.child_code || childMeta.code || '').trim().toUpperCase(),
    childName: childMeta.name || undefined,
    parentCode: String(row.parent_code || childMeta.parentCode || '').trim().toUpperCase() || undefined,
    paymentMethod: journal ? 'ledgers' : method,
    method: journal ? 'ledgers' : (method || 'cash'),
    type: journal ? 'Ledgers' : method === 'bank' ? 'Bank' : method === 'cash' ? 'Cash' : '',
  };
}

/** Parent CoA codes whose ledger debits feed Cash/Bank expenses on the Z Report. */
export { COA_CASH_BANK_EXPENSE_PARENTS as COA_Z_REPORT_EXPENSE_PARENTS };

const Z_EXPENSE_PARENT_NAMES = {
  ADM: 'Admin Expenses',
  SEL: 'Selling Expenses',
  OEXP: 'Other Expenses',
  LTL: 'Long Term Loans',
  STL: 'Short Term Loans',
};

/** Child + parent CoA codes feeding the Z expense / loan buckets (one query). */
async function loadZExpenseChildMeta(gid) {
  const zParents = [...COA_CASH_BANK_EXPENSE_PARENTS, ...COA_Z_REPORT_LOAN_PARENTS];

  const { data: allChildren, error: childError } = await supabase
    .from('coa_child_accounts')
    .select('code, name, parent_code')
    .eq('gym_id', gid);

  if (childError) {
    throwIfCoaMissing(childError);
    throw childError;
  }

  const prefixOf = (code) => {
    if (code === 'ADM' || code.startsWith('ADM-')) return 'ADM';
    if (code === 'SEL' || code.startsWith('SEL-')) return 'SEL';
    if (code === 'OEXP' || code.startsWith('OEXP-')) return 'OEXP';
    if (code === 'LTL' || code.startsWith('LTL-')) return 'LTL';
    if (code === 'STL' || code.startsWith('STL-')) return 'STL';
    return '';
  };

  const childMeta = new Map();
  (allChildren || []).forEach((row) => {
    const code = String(row.code || '').toUpperCase();
    if (!code) return;
    const declaredParent = String(row.parent_code || '').toUpperCase();
    const parentCode = zParents.includes(declaredParent) ? declaredParent : prefixOf(code);
    if (!parentCode) return;
    childMeta.set(code, {
      code,
      name: String(row.name || '').trim(),
      parentCode,
    });
  });

  // Include parent codes so posts on ADM / SEL / OEXP / LTL / STL themselves are included
  zParents.forEach((parentCode) => {
    const code = String(parentCode || '').toUpperCase();
    if (!childMeta.has(code)) {
      childMeta.set(code, {
        code,
        name: Z_EXPENSE_PARENT_NAMES[code] || code,
        parentCode: code,
      });
    }
  });

  return childMeta;
}

/** Shared row → Z expense mapping so day and range paths stay identical. */
function mapZExpenseLedgerRow(row, childMeta, fallbackDay) {
  const code = String(row.child_code || '').toUpperCase();
  const meta = childMeta.get(code);
  if (!meta) return null;
  const entry = rowToCoaLedgerEntry(row, meta);
  const amount = Number(entry.debit) || 0;
  if (amount <= 0) return null;

  // Prefer stored Type (payment_method); fall back to voucher ref (BPV → bank, CPV → cash)
  const tender = resolveLedgerTender(row) || 'cash';
  const parentCode = meta.parentCode || entry.parentCode || '';
  const day = entry.date || calendarDateKey(row.entry_date) || fallbackDay;

  return {
    id: entry.id || `coa-${code}-${day}-${amount}`,
    accountTitle: entry.childName || meta.name || Z_EXPENSE_PARENT_NAMES[parentCode] || 'Expense',
    description: entry.description || '',
    category: Z_EXPENSE_PARENT_NAMES[parentCode] || parentCode || 'Other',
    parentCode,
    amount,
    date: day,
    status: 'paid',
    method: tender,
    paymentMethod: tender,
    type: tender === 'bank' ? 'Bank' : 'Cash',
    accountCode: code,
    code,
    source: 'coa_ledger',
    ref: entry.ref || row.ref || '',
  };
}

/**
 * Load Admin / Selling / Other expense + Long/Short Term Loan ledger debits for a date (Z Report).
 * Splits into cash vs bank using payment_method (Type), with voucher-ref fallback.
 * Gym expenses → GYM CASH/BANK; loans → PEARL GROUP (auto).
 */
export async function fetchCoaZReportExpenses(date, explicitGymId) {
  const day = String(date || '').slice(0, 10);
  if (!day) return [];
  return fetchCoaZReportExpensesForDateRange(day, day, explicitGymId);
}

/**
 * CoA Z-report expense/loan debits for an inclusive date range.
 * Two queries total (children + ledger range) regardless of range length.
 */
export async function fetchCoaZReportExpensesForDateRange(dateFrom, dateTo, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const from = String(dateFrom || '').slice(0, 10);
  const to = String(dateTo || dateFrom || '').slice(0, 10);
  if (!gid || !/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return [];
  const start = from <= to ? from : to;
  const end = from <= to ? to : from;

  const childMeta = await loadZExpenseChildMeta(gid);
  if (!childMeta.size) return [];

  const entryRows = await fetchAllLedgerEntriesInRange(gid, start, end);

  return entryRows
    .map((row) => mapZExpenseLedgerRow(row, childMeta, start))
    .filter(Boolean);
}

const LEDGER_RANGE_COLUMNS = 'id, entry_date, child_code, parent_code, description, debit, credit, ref, payment_method';

/** Paged ledger rows for a gym + inclusive entry_date window. */
async function fetchAllLedgerEntriesInRange(gid, start, end) {
  const out = [];
  const pageSize = 1000;
  let offset = 0;
  let columns = LEDGER_RANGE_COLUMNS;
  for (;;) {
    const { data, error } = await supabase
      .from('coa_ledger_entries')
      .select(columns)
      .eq('gym_id', gid)
      .gte('entry_date', start)
      .lte('entry_date', end)
      .order('entry_date', { ascending: true })
      .range(offset, offset + pageSize - 1);

    if (error) {
      // Older schemas may not have every column — retry once with a full select
      if (columns !== '*' && /column|does not exist/i.test(error.message || '')) {
        columns = '*';
        continue;
      }
      throwIfCoaMissing(error);
      throw error;
    }
    out.push(...(data || []));
    if ((data || []).length < pageSize) break;
    offset += pageSize;
  }
  return out;
}

/**
 * Long Term / Short Term Loans ledger for a day (all LTL/STL children).
 * Prefer Daily Z auto via fetchCoaZReportExpenses + sumZReportLoanExpensesByTender;
 * this helper remains for Pearl-only net totals if needed.
 */
export async function fetchCoaPearlGroupZReportAmounts(date, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(date || '').slice(0, 10);
  const empty = { cash: 0, bank: 0, cashIn: 0, bankIn: 0, cashOut: 0, bankOut: 0 };
  if (!gid || !day) return empty;

  const { data: children, error: childError } = await supabase
    .from('coa_child_accounts')
    .select('code, name, parent_code')
    .eq('gym_id', gid)
    .or('parent_code.eq.LTL,parent_code.eq.STL,code.ilike.LTL-%,code.ilike.STL-%');

  if (childError) {
    throwIfCoaMissing(childError);
    throw childError;
  }

  const loanCodes = [...new Set(
    (children || [])
      .filter((row) => {
        const parent = String(row.parent_code || '').trim().toUpperCase();
        const code = String(row.code || '').trim().toUpperCase();
        return parent === 'LTL' || parent === 'STL'
          || code.startsWith('LTL-') || code.startsWith('STL-')
          || code === 'LTL' || code === 'STL';
      })
      .map((row) => String(row.code || '').trim().toUpperCase())
      .filter(Boolean),
  )];

  // Also allow posting directly on parent codes
  ['LTL', 'STL'].forEach((c) => {
    if (!loanCodes.includes(c)) loanCodes.push(c);
  });

  if (!loanCodes.length) return empty;

  const { data: entries, error: entryError } = await supabase
    .from('coa_ledger_entries')
    .select('*')
    .eq('gym_id', gid)
    .eq('entry_date', day)
    .in('child_code', loanCodes);

  if (entryError) {
    throwIfCoaMissing(entryError);
    throw entryError;
  }

  let cashIn = 0;
  let bankIn = 0;
  let cashOut = 0;
  let bankOut = 0;

  (entries || []).forEach((row) => {
    const debit = Number(row.debit) || 0;
    const credit = Number(row.credit) || 0;
    const tender = resolveLedgerTender(row) || 'cash';
    const isBank = tender === 'bank';
    if (debit > 0) {
      if (isBank) bankOut += debit;
      else cashOut += debit;
    }
    if (credit > 0) {
      if (isBank) bankIn += credit;
      else cashIn += credit;
    }
  });

  // Net amount shown on the PEARL GROUP summary row (out − in; loans are typically out)
  return {
    cashIn,
    bankIn,
    cashOut,
    bankOut,
    cash: Math.max(0, cashOut - cashIn),
    bank: Math.max(0, bankOut - bankIn),
  };
}

/**
 * Load cash-only CoA ledger entries for a date from every parent + child
 * (for Petty Cash Detail). Debits → Payments, credits → Receipts.
 * Bank/card/QR posts are excluded.
 */
export async function fetchCoaPettyCashLedgerPayments(date, explicitGymId) {
  const day = String(date || '').slice(0, 10);
  if (!day) return [];
  return fetchCoaPettyCashLedgerPaymentsForDateRange(day, day, explicitGymId);
}

/**
 * Petty-cash ledger rows for an inclusive date range.
 * Three queries total (parents + children + ledger range) regardless of range length.
 */
export async function fetchCoaPettyCashLedgerPaymentsForDateRange(dateFrom, dateTo, explicitGymId, opts = {}) {
  const gid = await resolveGymId(explicitGymId);
  const from = String(dateFrom || '').slice(0, 10);
  const to = String(dateTo || dateFrom || '').slice(0, 10);
  if (!gid || !/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return [];
  const rangeStart = from <= to ? from : to;
  const rangeEnd = from <= to ? to : from;
  const day = rangeStart;
  const bankBook = String(opts?.book || 'cash').toLowerCase() === 'bank';

  let parents = [];
  try {
    const { data: parentRows, error: parentError } = await supabase
      .from('coa_parent_accounts')
      .select('code, name')
      .eq('gym_id', gid)
      .order('sort_order', { ascending: true });

    if (parentError) {
      throwIfCoaMissing(parentError);
      throw parentError;
    }

    parents = (parentRows || [])
      .map((row) => ({
        code: String(row.code || '').trim().toUpperCase(),
        name: String(row.name || '').trim(),
      }))
      .filter((p) => p.code);
  } catch (err) {
    if (!/coa_parent_accounts|does not exist/i.test(String(err?.message || ''))) {
      throw err;
    }
  }

  if (!parents.length) {
    parents = (COA_PARENT_ACCOUNTS || []).map((p) => ({
      code: String(p.code || '').trim().toUpperCase(),
      name: String(p.name || '').trim(),
    })).filter((p) => p.code);
  }

  const parentName = Object.fromEntries(parents.map((p) => [p.code, p.name]));
  (COA_PARENT_ACCOUNTS || []).forEach((p) => {
    const code = String(p.code || '').trim().toUpperCase();
    if (code && !parentName[code]) parentName[code] = String(p.name || '').trim();
  });

  const parentCodes = new Set(parents.map((p) => p.code));

  const { data: children, error: childError } = await supabase
    .from('coa_child_accounts')
    .select('code, name, parent_code')
    .eq('gym_id', gid);

  if (childError) {
    throwIfCoaMissing(childError);
    throw childError;
  }

  const childMeta = new Map();
  (children || []).forEach((row) => {
    const code = String(row.code || '').toUpperCase();
    if (!code) return;
    let parentCode = String(row.parent_code || '').toUpperCase();
    if (!parentCodes.has(parentCode)) {
      for (const pc of parentCodes) {
        if (code === pc || code.startsWith(`${pc}-`)) {
          parentCode = pc;
          break;
        }
      }
    }
    if (!parentCodes.has(parentCode)) return;
    childMeta.set(code, {
      code,
      name: String(row.name || '').trim(),
      parentCode,
    });
  });

  parentCodes.forEach((parentCode) => {
    if (!childMeta.has(parentCode)) {
      childMeta.set(parentCode, {
        code: parentCode,
        name: parentName[parentCode] || parentCode,
        parentCode,
      });
    }
  });

  const codes = [...childMeta.keys()];
  if (!codes.length) return [];

  const entryRows = await fetchAllLedgerEntriesInRange(gid, rangeStart, rangeEnd);

  const out = [];
  entryRows.forEach((row) => {
    const code = String(row.child_code || '').toUpperCase();
    if (!childMeta.has(code)) return;
    const ref = String(row.ref || '').trim();
    const voucherCash = isCashVoucherRef(ref);
    const cashInHand = isCashInHandCode(code);
    const parentCodeHint = String(childMeta.get(code)?.parentCode || '').toUpperCase();
    const voucherBank = isBankVoucherRef(ref);
    const cashAtBank = isCashAtBankCode(code, parentCodeHint);

    if (bankBook) {
      if (voucherBank && !cashAtBank) return;
      const tender = resolveLedgerTender(row);
      if (tender !== 'bank' && !voucherBank && !cashAtBank) return;
    } else {
      // CPV/CRV: single cash-book effect from Cash in Hand only (skip expense/loan legs)
      if (voucherCash && !cashInHand) return;
      const tender = resolveLedgerTender(row);
      // Cash in Hand / cash vouchers always count as cash even if payment_method blank
      if (tender !== 'cash' && !voucherCash && !cashInHand) return;
    }

    const meta = childMeta.get(code) || { code, name: code, parentCode: '' };
    const entry = rowToCoaLedgerEntry(row, meta);
    const debit = Number(entry.debit) || 0;
    const credit = Number(entry.credit) || 0;
    if (debit <= 0 && credit <= 0) return;

    const parentCode = meta.parentCode || entry.parentCode || '';
    const rowDay = entry.date || calendarDateKey(row.entry_date) || day;
    const bookTender = bankBook ? 'bank' : 'cash';
    const base = {
      accountTitle: entry.childName || meta.name || parentName[parentCode] || (bankBook ? 'Bank' : 'Cash'),
      description: entry.description || '',
      category: parentName[parentCode] || parentCode || 'Other',
      parentCode,
      date: rowDay,
      status: 'paid',
      method: bookTender,
      paymentMethod: bookTender,
      type: bankBook ? 'Bank' : 'Cash',
      accountCode: code,
      code,
      source: 'coa_ledger',
      ref: entry.ref || ref || '',
      debit,
      credit,
    };

    // Cash book (CIH / CPV / CRV) or Bank book (CAB / BPV / BRV): Debit = Receipts, Credit = Payments
    if (bankBook ? (cashAtBank || voucherBank) : (cashInHand || voucherCash)) {
      if (debit > 0) {
        out.push({
          ...base,
          id: entry.id ? `${entry.id}-dr` : `coa-petty-${code}-${rowDay}-dr-${debit}`,
          amount: debit,
          debit,
          credit: 0,
          side: 'debit',
          pettyColumn: 'receipts',
        });
      }
      if (credit > 0) {
        out.push({
          ...base,
          id: entry.id ? `${entry.id}-cr` : `coa-petty-${code}-${rowDay}-cr-${credit}`,
          amount: credit,
          debit: 0,
          credit,
          side: 'credit',
          pettyColumn: 'payments',
        });
      }
      return;
    }

    // Other cash ledger lines (non-voucher expenses): debit → Payments
    if (debit > 0) {
      out.push({
        ...base,
        id: entry.id ? `${entry.id}-dr` : `coa-petty-${code}-${rowDay}-dr-${debit}`,
        amount: debit,
        debit,
        credit: 0,
        side: 'debit',
        pettyColumn: 'payments',
      });
    }
  });
  return out;
}

export async function fetchCoaBankLedgerPaymentsForDateRange(dateFrom, dateTo, explicitGymId) {
  return fetchCoaPettyCashLedgerPaymentsForDateRange(dateFrom, dateTo, explicitGymId, { book: 'bank' });
}

/** Seed default parent accounts for this gym; also insert any newly added defaults. */
export async function ensureCoaParentsSeeded(explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) return [];

  const { data, error } = await supabase
    .from('coa_parent_accounts')
    .select('*')
    .eq('gym_id', gid)
    .order('sort_order', { ascending: true });

  if (error) {
    throwIfCoaMissing(error);
    throw error;
  }

  const existing = (data || []).map(rowToCoaParent).filter((p) => p.code && p.name);
  const existingCodes = new Set(existing.map((p) => p.code));

  const missing = (COA_PARENT_ACCOUNTS || [])
    .map((p, index) => ({
      gym_id: gid,
      code: String(p.code || '').trim().toUpperCase(),
      name: String(p.name || '').trim(),
      account_type: String(p.type || 'asset').trim().toLowerCase(),
      sort_order: index + 1,
    }))
    .filter((p) => p.code && p.name && !existingCodes.has(p.code));

  if (missing.length) {
    const { data: inserted, error: insertError } = await supabase
      .from('coa_parent_accounts')
      .insert(stampCreatedAt(missing))
      .select('*');

    if (insertError && !/duplicate|unique/i.test(insertError.message || '')) {
      throwIfCoaMissing(insertError);
      throw insertError;
    }

    invalidateCache(`gym:${gid}:`);

    if (inserted?.length) {
      const extras = inserted.map(rowToCoaParent).filter((p) => p.code && p.name);
      const byCode = new Map([...existing, ...extras].map((p) => [p.code, p]));
      // Prefer default sort order
      return (COA_PARENT_ACCOUNTS || [])
        .map((p) => byCode.get(String(p.code).toUpperCase()))
        .filter(Boolean)
        .concat([...byCode.values()].filter((p) => !(COA_PARENT_ACCOUNTS || []).some((d) => String(d.code).toUpperCase() === p.code)));
    }

    // Re-fetch after race
    const { data: again } = await supabase
      .from('coa_parent_accounts')
      .select('*')
      .eq('gym_id', gid)
      .order('sort_order', { ascending: true });
    return (again || []).map(rowToCoaParent).filter((p) => p.code && p.name);
  }

  if (existing.length) {
    // Keep seeded defaults first in configured order
    const byCode = new Map(existing.map((p) => [p.code, p]));
    const ordered = (COA_PARENT_ACCOUNTS || [])
      .map((p) => byCode.get(String(p.code).toUpperCase()))
      .filter(Boolean);
    const extras = existing.filter((p) => !(COA_PARENT_ACCOUNTS || []).some((d) => String(d.code).toUpperCase() === p.code));
    return [...ordered, ...extras];
  }

  return existing;
}

/** One-time migrate any localStorage children into Supabase. */
async function migrateLocalCoaChildren(gid, parents) {
  const local = loadCoaChildAccounts(gid);
  if (!local.length) return;

  const parentCodes = new Set(parents.map((p) => p.code));
  const { data: existingChildren } = await supabase
    .from('coa_child_accounts')
    .select('code')
    .eq('gym_id', gid);
  const used = new Set((existingChildren || []).map((r) => String(r.code || '').toUpperCase()));

  const toInsert = [];
  const entriesToInsert = [];

  local.forEach((child) => {
    if (!parentCodes.has(child.parentCode) || used.has(child.code)) return;
    used.add(child.code);
    toInsert.push({
      gym_id: gid,
      parent_code: child.parentCode,
      code: child.code,
      name: child.name,
      description: child.description || null,
    });
    (child.entries || []).forEach((entry) => {
      entriesToInsert.push({
        gym_id: gid,
        child_code: child.code,
        entry_date: entry.date || localDateKey(),
        description: entry.description || null,
        debit: Number(entry.debit) || 0,
        credit: Number(entry.credit) || 0,
        ref: entry.ref && entry.ref !== '—' ? entry.ref : null,
      });
    });
  });

  if (toInsert.length) {
    const { error } = await runGenericMutation({
      table: 'coa_child_accounts',
      op: 'insert',
      payload: stampCreatedAt(toInsert),
      source: 'finance',
    });
    if (!error && entriesToInsert.length) {
      await runGenericMutation({
        table: 'coa_ledger_entries',
        op: 'insert',
        payload: stampCreatedAt(entriesToInsert),
        source: 'finance',
      });
    }
    if (!error) {
      try { saveCoaChildAccounts(gid, []); } catch { /* ignore */ }
    }
  }
}

/** Seed Cash in Hand / Other Receivables tender children when missing. */
async function ensureCoaTenderChildrenSeeded(gid) {
  if (!gid) return;

  const defs = [
    ...COA_CIH_CHILDREN.map((d) => ({ ...d, parentCode: 'CIH' })),
    ...COA_ORCV_CHILDREN.map((d) => ({ ...d, parentCode: 'ORCV' })),
    ...COA_PEARL_CHILDREN.map((d) => ({ ...d, parentCode: 'PEARL' })),
  ];

  const { data: existing, error } = await supabase
    .from('coa_child_accounts')
    .select('code, name, parent_code')
    .eq('gym_id', gid)
    .in('parent_code', ['CIH', 'ORCV', 'PEARL']);

  if (error) {
    throwIfCoaMissing(error);
    throw error;
  }

  const rows = existing || [];
  const usedCodes = new Set(rows.map((r) => String(r.code || '').toUpperCase()));
  const toInsert = [];

  defs.forEach((def) => {
    const code = String(def.code || '').toUpperCase();
    if (usedCodes.has(code)) return;

    const aliases = new Set([
      normalizeCoaAccountLabel(def.name),
      ...(def.aliases || []).map(normalizeCoaAccountLabel),
    ].filter(Boolean));

    const nameMatch = rows.some((row) => (
      String(row.parent_code || '').toUpperCase() === def.parentCode
      && aliases.has(normalizeCoaAccountLabel(row.name))
    ));
    if (nameMatch) return;

    usedCodes.add(code);
    toInsert.push({
      gym_id: gid,
      parent_code: def.parentCode,
      code,
      name: def.name,
      description: def.parentCode === 'PEARL'
        ? `${def.name} (Chart of Accounts)`
        : `Live ${def.name} payments`,
    });
  });

  if (!toInsert.length) return;

  const { error: insertError } = await supabase
    .from('coa_child_accounts')
    .insert(stampCreatedAt(toInsert));

  if (insertError && !/duplicate|unique/i.test(insertError.message || '')) {
    throwIfCoaMissing(insertError);
    throw insertError;
  }

  invalidateCache(`gym:${gid}:`);
}

/** Page through every coa_ledger_entries row for a gym (PostgREST default cap is 1000). */
async function fetchAllCoaLedgerEntriesForGym(gid) {
  const out = [];
  const pageSize = 1000;
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('coa_ledger_entries')
      .select('*')
      .eq('gym_id', gid)
      .order('entry_date', { ascending: false })
      .order('id', { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) {
      throwIfCoaMissing(error);
      throw error;
    }
    out.push(...(data || []));
    if ((data || []).length < pageSize) break;
    offset += pageSize;
    if (offset > 500000) break;
  }
  return out;
}

/** Full parent → children → entries tree from Supabase. */
export async function fetchCoaTree(explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) return [];

  const parents = await ensureCoaParentsSeeded(gid);

  try {
    await migrateLocalCoaChildren(gid, parents);
  } catch {
    // Migration is best-effort
  }

  try {
    await ensureCoaTenderChildrenSeeded(gid);
  } catch {
    // Seeding tender children is best-effort
  }

  const [{ data: children, error: childError }, entries, voucherDateByRef] = await Promise.all([
    supabase
      .from('coa_child_accounts')
      .select('*')
      .eq('gym_id', gid)
      .order('code', { ascending: true }),
    fetchAllCoaLedgerEntriesForGym(gid),
    fetchVoucherDateByRef(gid).catch(() => new Map()),
  ]);

  if (childError) {
    throwIfCoaMissing(childError);
    throw childError;
  }

  // Best-effort: stored ledger day must match voucher Vch. Date
  try {
    await alignLedgerDatesToVouchers(gid, voucherDateByRef);
  } catch {
    // non-blocking
  }

  const childNameByCode = new Map();
  const parentCodeByChild = new Map();
  (children || []).forEach((row) => {
    const code = String(row.code || '').toUpperCase();
    childNameByCode.set(code, String(row.name || '').trim());
    parentCodeByChild.set(code, String(row.parent_code || '').toUpperCase());
  });

  // Prefer matching named tender children (user-created) over seeded codes.
  const tenderTargetByAlias = new Map();
  Object.entries(COA_TENDER_CODE_ALIASES).forEach(([from, to]) => {
    tenderTargetByAlias.set(String(from).toUpperCase(), String(to).toUpperCase());
  });
  (children || []).forEach((row) => {
    const parent = String(row.parent_code || '').toUpperCase();
    const code = String(row.code || '').toUpperCase();
    const name = normalizeCoaAccountLabel(row.name);
    if (parent === 'CIH' && (name === 'cash in hand' || name === 'cash')) {
      tenderTargetByAlias.set('PAY-CASH', code);
      tenderTargetByAlias.set('CIH-CASH', code);
    }
    if (parent === 'ORCV') {
      if (name.includes('bank')) {
        tenderTargetByAlias.set('PAY-BANK', code);
        tenderTargetByAlias.set('ORCV-BANK', code);
      } else if (name.includes('card') || name.includes('credit')) {
        tenderTargetByAlias.set('PAY-CARD', code);
        tenderTargetByAlias.set('ORCV-CARD', code);
      } else if (name.includes('qr')) {
        tenderTargetByAlias.set('PAY-QR', code);
        tenderTargetByAlias.set('ORCV-QR', code);
      }
    }
  });

  const resolveEntryChildCode = (rawCode) => {
    const code = String(rawCode || '').toUpperCase();
    if (childNameByCode.has(code)) return code;
    const mapped = tenderTargetByAlias.get(code);
    if (mapped && childNameByCode.has(mapped)) return mapped;
    return code;
  };

  const entriesByChild = new Map();
  (entries || []).forEach((row) => {
    const code = resolveEntryChildCode(row.child_code);
    const list = entriesByChild.get(code) || [];
    const mapped = rowToCoaLedgerEntry(row, {
      code,
      name: childNameByCode.get(code),
      parentCode: parentCodeByChild.get(code),
    }, voucherDateByRef);
    // If we repaired the date in DB, also prefer voucher date on the in-memory row
    const want = voucherDateByRef.get(String(row.ref || '').trim().toUpperCase());
    if (want) mapped.date = want;
    list.push(mapped);
    entriesByChild.set(code, list);
  });

  const childRows = (children || []).map((row) => {
    const code = String(row.code || '').toUpperCase();
    return rowToCoaChild(row, entriesByChild.get(code) || []);
  });

  return parents.map((parent) => ({
    ...parent,
    children: childRows
      .filter((c) => c.parentCode === parent.code)
      .sort((a, b) => String(a.code).localeCompare(String(b.code))),
  }));
}

export async function createCoaChildRemote(form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');

  const parentCode = String(form.parentCode || '').trim().toUpperCase();
  const name = String(form.name || '').trim();
  if (!parentCode) throw new Error('Select a valid parent account first.');
  if (!name) throw new Error('Child account name is required.');

  let code = String(form.code || '').trim().toUpperCase();
  if (!code) {
    const { data: existing } = await supabase
      .from('coa_child_accounts')
      .select('code')
      .eq('gym_id', gid);
    const used = new Set((existing || []).map((r) => String(r.code || '').toUpperCase()));
    const prefix = parentCode.slice(0, 4);
    for (let n = 1; n < 1000; n += 1) {
      const next = `${prefix}-${String(n).padStart(3, '0')}`;
      if (!used.has(next)) {
        code = next;
        break;
      }
    }
    if (!code) code = `${prefix}-${Date.now().toString().slice(-4)}`;
  }

  const { data, error } = await supabase
    .from('coa_child_accounts')
    .insert(stampCreatedAt({
      gym_id: gid,
      parent_code: parentCode,
      code,
      name,
      description: String(form.description || '').trim() || null,
    }))
    .select('*')
    .single();

  if (error) {
    throwIfCoaMissing(error);
    if (/duplicate|unique/i.test(error.message || '')) {
      throw new Error(`Child account code ${code} already exists.`);
    }
    if (/foreign key|coa_child_parent/i.test(error.message || '')) {
      throw new Error('Parent account not found in Supabase. Refresh and try again.');
    }
    throw error;
  }

  invalidateCache(`gym:${gid}:`);
  return rowToCoaChild(data, []);
}

export async function deleteCoaChildRemote(code, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  const needle = String(code || '').trim().toUpperCase();
  if (!needle) throw new Error('Child account code is required.');

  const { error: entryError } = await supabase
    .from('coa_ledger_entries')
    .delete()
    .eq('gym_id', gid)
    .eq('child_code', needle);
  if (entryError) {
    throwIfCoaMissing(entryError);
    throw entryError;
  }

  const { error } = await supabase
    .from('coa_child_accounts')
    .delete()
    .eq('gym_id', gid)
    .eq('code', needle);

  if (error) {
    throwIfCoaMissing(error);
    throw error;
  }

  invalidateCache(`gym:${gid}:`);
  return true;
}

export async function createCoaLedgerEntryRemote(childCode, form, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  const code = String(childCode || '').trim().toUpperCase();
  if (!code) throw new Error('Select a child account to post an entry.');

  const debit = Math.max(0, Number(form.debit) || 0);
  const credit = Math.max(0, Number(form.credit) || 0);
  if (debit <= 0 && credit <= 0) throw new Error('Enter a debit or credit amount.');

  const paymentMethod = normalizeExpensePaymentMethod(
    form.paymentMethod || form.method || 'cash',
  );

  const payload = {
    gym_id: gid,
    child_code: code,
    entry_date: calendarDateKey(form.date, localDateKey()),
    description: String(form.description || '').trim() || null,
    debit,
    credit,
    ref: String(form.ref || '').trim() || null,
    payment_method: paymentMethod,
  };

  let { data, error } = await supabase
    .from('coa_ledger_entries')
    .insert(stampCreatedAt(payload))
    .select('*')
    .single();

  // Older schemas may not have payment_method yet
  if (error && /payment_method/i.test(error.message || '')) {
    delete payload.payment_method;
    ({ data, error } = await supabase
      .from('coa_ledger_entries')
      .insert(stampCreatedAt(payload))
      .select('*')
      .single());
  }

  if (error) {
    throwIfCoaMissing(error);
    if (/foreign key|coa_ledger_child/i.test(error.message || '')) {
      throw new Error('Child account not found in Supabase.');
    }
    if (/payment_method/i.test(error.message || '')) {
      throw new Error(
        'Payment method column missing. Open Supabase → SQL Editor, run supabase/fix_expense_payment_method.sql, then retry.',
      );
    }
    throw error;
  }

  invalidateCache(`gym:${gid}:`);
  return rowToCoaLedgerEntry(data, { code });
}

function throwIfVouchersMissing(error) {
  const msg = String(error?.message || '');
  // Only treat real missing-table / schema-cache misses — not every finance_vouchers error
  const missing =
    /Could not find the ['"]?(finance_vouchers|finance_voucher_lines)/i.test(msg)
    || /relation ['"]?(public\.)?(finance_vouchers|finance_voucher_lines)['"]? does not exist/i.test(msg)
    || (/schema cache/i.test(msg) && /finance_voucher/i.test(msg));
  if (missing) {
    throw new Error(
      'Voucher tables not found. Open Supabase → SQL Editor, run supabase/fix_finance_vouchers.sql, then refresh.',
    );
  }
}

/** Cash vouchers → cash; bank vouchers → bank; journal has no tender type. */
function paymentMethodForVoucherType(voucherType) {
  const t = String(voucherType || '').trim().toLowerCase();
  if (t === 'cash_payment' || t === 'cash_receipt' || t === 'cpv' || t === 'crv') {
    return 'cash';
  }
  if (t === 'bank_payment' || t === 'bank_receipt' || t === 'bpv' || t === 'brv') {
    return 'bank';
  }
  return null;
}

async function insertVoucherLedgerRows(gid, ledgerRows) {
  if (!ledgerRows.length) return;
  let { error } = await runGenericMutation({
    table: 'coa_ledger_entries',
    op: 'insert',
    payload: stampCreatedAt(ledgerRows),
    source: 'finance',
  });
  // Older schemas may not have payment_method yet
  if (error && /payment_method/i.test(error.message || '')) {
    const withoutMethod = stampCreatedAt(ledgerRows.map(({ payment_method, ...rest }) => rest));
    ({ error } = await runGenericMutation({
      table: 'coa_ledger_entries',
      op: 'insert',
      payload: withoutMethod,
      source: 'finance',
    }));
  }
  if (error) {
    throwIfCoaMissing(error);
    throw new Error(
      `Voucher saved but ledger post failed: ${error.message || 'unknown error'}`,
    );
  }
}

/** Map voucher_no → exact voucher_date for CoA parent/child ledger display & repair. */
async function fetchVoucherDateByRef(gid) {
  const map = new Map();
  if (!gid) return map;
  const pageSize = 1000;
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('finance_vouchers')
      .select('voucher_no, voucher_date')
      .eq('gym_id', gid)
      .order('voucher_date', { ascending: false })
      .range(offset, offset + pageSize - 1);
    if (error) {
      throwIfVouchersMissing(error);
      return map;
    }
    (data || []).forEach((row) => {
      const ref = String(row.voucher_no || '').trim();
      const day = calendarDateKey(row.voucher_date);
      if (!ref || !day) return;
      map.set(ref.toUpperCase(), day);
      map.set(ref, day);
    });
    if ((data || []).length < pageSize) break;
    offset += pageSize;
    if (offset > 100000) break;
  }
  return map;
}

/**
 * Keep coa_ledger_entries.entry_date equal to finance_vouchers.voucher_date
 * for cash/bank received & payment (and journal) voucher refs.
 */
async function alignLedgerDatesToVouchers(gid, voucherDateByRef) {
  if (!gid || !(voucherDateByRef instanceof Map) || voucherDateByRef.size === 0) return;
  const refs = [...new Set(
    [...voucherDateByRef.keys()].filter((k) => k === String(k).toUpperCase()),
  )];
  if (!refs.length) return;

  const { data: entries, error } = await supabase
    .from('coa_ledger_entries')
    .select('id, ref, entry_date')
    .eq('gym_id', gid)
    .in('ref', refs);
  if (error || !entries?.length) return;

  const updates = [];
  entries.forEach((row) => {
    const ref = String(row.ref || '').trim();
    const want = voucherDateByRef.get(ref.toUpperCase()) || voucherDateByRef.get(ref);
    const have = calendarDateKey(row.entry_date);
    if (!want || !row.id || want === have) return;
    updates.push({ id: row.id, entry_date: want });
  });

  await Promise.all(
    updates.map((patch) => (
      supabase
        .from('coa_ledger_entries')
        .update({ entry_date: patch.entry_date })
        .eq('id', patch.id)
        .eq('gym_id', gid)
    )),
  );
}

function rowToFinanceVoucher(row, lines = []) {
  return {
    id: row.id,
    type: row.voucher_type,
    voucherNo: row.voucher_no,
    date: calendarDateKey(row.voucher_date),
    narration: row.narration || '',
    chequeNo: row.cheque_no || '',
    totalDebit: Number(row.total_debit) || 0,
    totalCredit: Number(row.total_credit) || 0,
    status: row.status || 'posted',
    createdAt: row.created_at,
    lines: (lines || []).map((line) => ({
      id: line.id,
      lineNo: line.line_no,
      accountCode: line.account_code,
      accountTitle: line.account_title || '',
      description: line.description || '',
      debit: Number(line.debit) || 0,
      credit: Number(line.credit) || 0,
    })),
  };
}

export async function suggestNextVoucherNo(voucherType, prefix, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) return `${prefix}-0001`;
  const { data, error } = await supabase
    .from('finance_vouchers')
    .select('voucher_no')
    .eq('gym_id', gid)
    .eq('voucher_type', voucherType)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) {
    throwIfVouchersMissing(error);
    return `${prefix}-0001`;
  }
  let max = 0;
  (data || []).forEach((row) => {
    const match = String(row.voucher_no || '').match(/(\d+)\s*$/);
    if (match) max = Math.max(max, Number(match[1]) || 0);
  });
  return `${prefix}-${String(max + 1).padStart(4, '0')}`;
}

export async function fetchFinanceVouchers(voucherType, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) return [];
  const { data: headers, error } = await supabase
    .from('finance_vouchers')
    .select('*')
    .eq('gym_id', gid)
    .eq('voucher_type', voucherType)
    .order('voucher_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) {
    throwIfVouchersMissing(error);
    throw error;
  }
  if (!headers?.length) return [];

  // Keep parent/child ledger entry_date in sync with this voucher type's Vch. Date
  try {
    const voucherDateByRef = new Map();
    headers.forEach((row) => {
      const ref = String(row.voucher_no || '').trim();
      const day = calendarDateKey(row.voucher_date);
      if (!ref || !day) return;
      voucherDateByRef.set(ref.toUpperCase(), day);
      voucherDateByRef.set(ref, day);
    });
    await alignLedgerDatesToVouchers(gid, voucherDateByRef);
  } catch {
    // non-blocking
  }

  const ids = headers.map((h) => h.id);
  const { data: lines, error: lineError } = await supabase
    .from('finance_voucher_lines')
    .select('*')
    .eq('gym_id', gid)
    .in('voucher_id', ids)
    .order('line_no', { ascending: true });
  if (lineError) {
    throwIfVouchersMissing(lineError);
    throw lineError;
  }

  const linesByVoucher = new Map();
  (lines || []).forEach((line) => {
    const list = linesByVoucher.get(line.voucher_id) || [];
    list.push(line);
    linesByVoucher.set(line.voucher_id, list);
  });

  return headers.map((h) => rowToFinanceVoucher(h, linesByVoucher.get(h.id) || []));
}

/** Load one voucher by number for a voucher type (edit flow). */
export async function fetchFinanceVoucherByNo(voucherType, voucherNo, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const type = String(voucherType || '').trim();
  const no = String(voucherNo || '').trim();
  if (!gid || !type || !no) return null;

  const { data: header, error } = await supabase
    .from('finance_vouchers')
    .select('*')
    .eq('gym_id', gid)
    .eq('voucher_type', type)
    .ilike('voucher_no', no)
    .maybeSingle();

  if (error) {
    throwIfVouchersMissing(error);
    throw error;
  }
  if (!header) return null;

  const { data: lines, error: lineError } = await supabase
    .from('finance_voucher_lines')
    .select('*')
    .eq('gym_id', gid)
    .eq('voucher_id', header.id)
    .order('line_no', { ascending: true });

  if (lineError) {
    throwIfVouchersMissing(lineError);
    throw lineError;
  }

  return rowToFinanceVoucher(header, lines || []);
}

function normalizeVoucherLines(payloadLines = [], narration = '') {
  return (payloadLines || [])
    .map((line, index) => {
      const rawCode = String(line.accountCode || '').trim().toUpperCase();
      const accountCode = COA_TENDER_CODE_ALIASES[rawCode] || rawCode;
      return {
        line_no: index + 1,
        account_code: accountCode,
        account_title: String(line.accountTitle || '').trim() || null,
        description: String(line.description || narration || '').trim() || null,
        debit: Math.max(0, Number(line.debit) || 0),
        credit: Math.max(0, Number(line.credit) || 0),
      };
    })
    .filter((line) => line.account_code && (line.debit > 0 || line.credit > 0));
}

function assertVoucherBalanced(lines) {
  const totalDebit = lines.reduce((s, l) => s + l.debit, 0);
  const totalCredit = lines.reduce((s, l) => s + l.credit, 0);
  if (Math.abs(totalDebit - totalCredit) > 0.009) {
    throw new Error(
      `Voucher must balance. Debit ${totalDebit.toFixed(2)} ≠ Credit ${totalCredit.toFixed(2)}.`,
    );
  }
  return { totalDebit, totalCredit };
}

/**
 * Create a voucher + lines and post matching coa_ledger_entries.
 * @param {{ type, voucherNo, date, narration, lines: [{accountCode, accountTitle, description, debit, credit}] }} payload
 */
export async function createFinanceVoucher(payload, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');

  const type = String(payload.type || '').trim();
  const voucherNo = String(payload.voucherNo || '').trim();
  const voucherDate = calendarDateKey(payload.date, localDateKey());
  const narration = String(payload.narration || '').trim();
  const chequeNo = String(payload.chequeNo || '').trim();
  const lines = normalizeVoucherLines(payload.lines, narration);

  if (!type) throw new Error('Voucher type is required.');
  if (!voucherNo) throw new Error('Voucher number is required.');
  if (!lines.length) throw new Error('Add at least one voucher line with an amount.');
  if (!voucherDate) throw new Error('Voucher date is required.');

  const { totalDebit, totalCredit } = assertVoucherBalanced(lines);

  const headerPayload = {
    gym_id: gid,
    voucher_type: type,
    voucher_no: voucherNo,
    voucher_date: voucherDate,
    narration: narration || null,
    cheque_no: chequeNo || null,
    total_debit: totalDebit,
    total_credit: totalCredit,
    status: 'posted',
  };

  let { data: header, error: headerError } = await supabase
    .from('finance_vouchers')
    .insert(stampTimestamps(headerPayload))
    .select('*')
    .single();

  // Retry without cheque_no if column not migrated yet
  if (headerError && /cheque_no/i.test(headerError.message || '')) {
    const { cheque_no, ...withoutCheque } = headerPayload;
    ({ data: header, error: headerError } = await supabase
      .from('finance_vouchers')
      .insert(stampTimestamps(withoutCheque))
      .select('*')
      .single());
  }

  // Self-hosted: updated_at NOT NULL without default — retry with explicit stamp
  if (headerError && /updated_at/i.test(headerError.message || '')) {
    ({ data: header, error: headerError } = await supabase
      .from('finance_vouchers')
      .insert(stampTimestamps({
        ...headerPayload,
        updated_at: new Date().toISOString(),
      }))
      .select('*')
      .single());
  }

  if (headerError) {
    throwIfVouchersMissing(headerError);
    if (/unique|duplicate/i.test(headerError.message || '')) {
      throw new Error(`Voucher number ${voucherNo} already exists.`);
    }
    throw headerError;
  }

  const lineRows = lines.map((line) => ({
    gym_id: gid,
    voucher_id: header.id,
    ...line,
  }));

  const { data: insertedLines, error: lineError } = await supabase
    .from('finance_voucher_lines')
    .insert(stampCreatedAt(lineRows))
    .select('*');

  if (lineError) {
    await supabase.from('finance_vouchers').delete().eq('id', header.id);
    throwIfVouchersMissing(lineError);
    throw lineError;
  }

  const tender = paymentMethodForVoucherType(type);
  const ledgerRows = lines.map((line) => {
    const row = {
      gym_id: gid,
      child_code: line.account_code,
      entry_date: voucherDate,
      description: line.description || narration || `${type} ${voucherNo}`,
      debit: line.debit,
      credit: line.credit,
      ref: voucherNo,
    };
    if (tender) row.payment_method = tender;
    return row;
  });

  await insertVoucherLedgerRows(gid, ledgerRows);

  invalidateCache(`gym:${gid}:`);
  return rowToFinanceVoucher(header, insertedLines || []);
}

/**
 * Update an existing voucher (header + lines + ledger entries by voucher ref).
 */
export async function updateFinanceVoucher(voucherId, payload, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  const id = String(voucherId || '').trim();
  if (!id) throw new Error('Voucher not found.');

  const { data: existing, error: fetchError } = await supabase
    .from('finance_vouchers')
    .select('*')
    .eq('gym_id', gid)
    .eq('id', id)
    .maybeSingle();

  if (fetchError) {
    throwIfVouchersMissing(fetchError);
    throw fetchError;
  }
  if (!existing) throw new Error('Voucher not found.');

  const type = String(payload.type || existing.voucher_type || '').trim();
  const voucherNo = String(payload.voucherNo || existing.voucher_no || '').trim();
  const voucherDate = calendarDateKey(
    payload.date || existing.voucher_date,
    localDateKey(),
  );
  const narration = String(payload.narration || '').trim();
  const chequeNo = String(payload.chequeNo || '').trim();
  const lines = normalizeVoucherLines(payload.lines, narration);

  if (!lines.length) throw new Error('Add at least one voucher line with an amount.');
  if (!voucherDate) throw new Error('Voucher date is required.');
  const { totalDebit, totalCredit } = assertVoucherBalanced(lines);

  const oldRef = String(existing.voucher_no || '').trim();

  const headerPatch = {
    voucher_no: voucherNo,
    voucher_date: voucherDate,
    narration: narration || null,
    cheque_no: chequeNo || null,
    total_debit: totalDebit,
    total_credit: totalCredit,
    updated_at: new Date().toISOString(),
  };

  let { data: header, error: headerError } = await supabase
    .from('finance_vouchers')
    .update(headerPatch)
    .eq('id', id)
    .eq('gym_id', gid)
    .select('*')
    .single();

  if (headerError && /cheque_no/i.test(headerError.message || '')) {
    const { cheque_no, ...withoutCheque } = headerPatch;
    ({ data: header, error: headerError } = await supabase
      .from('finance_vouchers')
      .update(withoutCheque)
      .eq('id', id)
      .eq('gym_id', gid)
      .select('*')
      .single());
  }

  if (headerError) {
    throwIfVouchersMissing(headerError);
    if (/unique|duplicate/i.test(headerError.message || '')) {
      throw new Error(`Voucher number ${voucherNo} already exists.`);
    }
    throw headerError;
  }

  // Replace lines
  const { error: deleteLinesError } = await supabase
    .from('finance_voucher_lines')
    .delete()
    .eq('voucher_id', id)
    .eq('gym_id', gid);
  if (deleteLinesError) {
    throwIfVouchersMissing(deleteLinesError);
    throw deleteLinesError;
  }

  const lineRows = lines.map((line) => ({
    gym_id: gid,
    voucher_id: id,
    ...line,
  }));

  const { data: insertedLines, error: lineError } = await supabase
    .from('finance_voucher_lines')
    .insert(stampCreatedAt(lineRows))
    .select('*');
  if (lineError) {
    throwIfVouchersMissing(lineError);
    throw lineError;
  }

  // Replace ledger posts for this voucher ref (old number, then new if changed)
  const refs = [...new Set([oldRef, voucherNo].filter(Boolean))];
  if (refs.length) {
    await supabase
      .from('coa_ledger_entries')
      .delete()
      .eq('gym_id', gid)
      .in('ref', refs);
  }

  const tender = paymentMethodForVoucherType(type);
  const ledgerRows = lines.map((line) => {
    const row = {
      gym_id: gid,
      child_code: line.account_code,
      entry_date: voucherDate,
      description: line.description || narration || `${type} ${voucherNo}`,
      debit: line.debit,
      credit: line.credit,
      ref: voucherNo,
    };
    if (tender) row.payment_method = tender;
    return row;
  });

  await insertVoucherLedgerRows(gid, ledgerRows);

  invalidateCache(`gym:${gid}:`);
  return rowToFinanceVoucher(header || existing, insertedLines || []);
}

/**
 * Delete a voucher and all of its credit/debit lines + matching CoA ledger posts.
 * Ledger rows are keyed by voucher_no in `ref`, so both sides of the balanced
 * entry are removed together.
 */
export async function deleteFinanceVoucher(voucherId, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  if (!gid) throw new Error('No gym is linked to this account. Sign out and sign in again.');
  const id = String(voucherId || '').trim();
  if (!id) throw new Error('Voucher not found.');

  const { data: existing, error: fetchError } = await supabase
    .from('finance_vouchers')
    .select('id, voucher_no')
    .eq('gym_id', gid)
    .eq('id', id)
    .maybeSingle();

  if (fetchError) {
    throwIfVouchersMissing(fetchError);
    throw fetchError;
  }
  if (!existing) throw new Error('Voucher not found.');

  const voucherNo = String(existing.voucher_no || '').trim();
  if (voucherNo) {
    const { error: ledgerError } = await supabase
      .from('coa_ledger_entries')
      .delete()
      .eq('gym_id', gid)
      .eq('ref', voucherNo);
    if (ledgerError) {
      throwIfCoaMissing(ledgerError);
      throw ledgerError;
    }
  }

  // Lines cascade via ON DELETE CASCADE on finance_voucher_lines.voucher_id
  const { error: deleteError } = await supabase
    .from('finance_vouchers')
    .delete()
    .eq('id', id)
    .eq('gym_id', gid);

  if (deleteError) {
    throwIfVouchersMissing(deleteError);
    throw deleteError;
  }

  invalidateCache(`gym:${gid}:`);
  return { id, voucherNo };
}

function throwIfPettyCashMissing(error) {
  if (error && /petty_cash_reports.*schema cache|relation.*petty_cash_reports.*does not exist/i.test(error.message || '')) {
    throw new Error(
      'Petty cash table not found. Open Supabase → SQL Editor, run supabase/petty_cash_reports.sql, then refresh.',
    );
  }
}

/** Accept array or JSON-string jsonb from PostgREST. */
function parsePettyCashRowsJson(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    // Occasionally returned as object map
    return Object.values(raw);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return Object.values(parsed);
    } catch {
      return [];
    }
  }
  return [];
}

function normalizePettyCashRows(rows = []) {
  return parsePettyCashRowsJson(rows).map((row, index) => ({
    id: String(row?.id || `pc-${index}-${Date.now()}`),
    date: String(row?.date || row?.entry_date || '').slice(0, 10),
    description: String(row?.description || ''),
    receipts: row?.receipts === '' || row?.receipts == null ? '' : String(row.receipts),
    payments: row?.payments === '' || row?.payments == null ? '' : String(row.payments),
  }));
}

function mapPettyCashReportRow(data, day) {
  if (!data) {
    return { openingBalance: 0, rows: [], reportDate: day, id: null };
  }
  return {
    id: data.id || null,
    reportDate: String(data.report_date || day || '').slice(0, 10),
    openingBalance: Number(data.opening_balance) || 0,
    rows: normalizePettyCashRows(data.rows),
  };
}

/** Load petty cash opening + manual rows for a gym/date (Supabase, falls back empty). */
export async function fetchPettyCashReport(reportDate, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(reportDate || localDateKey()).slice(0, 10);
  if (!gid || !day) {
    return { openingBalance: 0, rows: [], reportDate: day, id: null };
  }

  let { data, error } = await supabase
    .from('petty_cash_reports')
    .select('*')
    .eq('gym_id', gid)
    .eq('report_date', day)
    .maybeSingle();

  // Fallback: date equality can miss if driver returns timestamptz — scan recent rows
  if (!error && !data) {
    const ranged = await supabase
      .from('petty_cash_reports')
      .select('*')
      .eq('gym_id', gid)
      .gte('report_date', day)
      .lte('report_date', day)
      .limit(1)
      .maybeSingle();
    data = ranged.data;
    error = ranged.error;
  }

  if (!error && !data) {
    const listed = await supabase
      .from('petty_cash_reports')
      .select('*')
      .eq('gym_id', gid)
      .order('report_date', { ascending: false })
      .limit(60);
    if (!listed.error && listed.data?.length) {
      data = listed.data.find((row) => String(row.report_date || '').slice(0, 10) === day) || null;
    } else if (listed.error) {
      error = listed.error;
    }
  }

  if (error) {
    throwIfPettyCashMissing(error);
    if (/does not exist|schema cache/i.test(error.message || '')) {
      return { openingBalance: 0, rows: [], reportDate: day, id: null };
    }
    throw error;
  }

  return mapPettyCashReportRow(data, day);
}

/** Upsert petty cash opening + rows for a gym/date. */
export async function savePettyCashReport({
  reportDate,
  openingBalance = 0,
  rows = [],
}, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(reportDate || localDateKey()).slice(0, 10);
  if (!gid) throw new Error('No gym is linked to this account.');
  if (!day) throw new Error('Report date is required.');

  const normalizedRows = normalizePettyCashRows(rows);
  const payload = {
    gym_id: gid,
    report_date: day,
    opening_balance: Math.max(0, Number(openingBalance) || 0),
    rows: normalizedRows,
    updated_at: new Date().toISOString(),
  };

  let { data, error } = await supabase
    .from('petty_cash_reports')
    .upsert(payload, { onConflict: 'gym_id,report_date' })
    .select('*')
    .maybeSingle();

  // Some projects reject upsert — update then insert
  if (error && /on conflict|unique|duplicate/i.test(error.message || '')) {
    const updated = await supabase
      .from('petty_cash_reports')
      .update({
        opening_balance: payload.opening_balance,
        rows: payload.rows,
        updated_at: payload.updated_at,
      })
      .eq('gym_id', gid)
      .eq('report_date', day)
      .select('*')
      .maybeSingle();
    if (!updated.error && updated.data) {
      data = updated.data;
      error = null;
    } else {
      const inserted = await supabase
        .from('petty_cash_reports')
        .insert(stampCreatedAt(payload))
        .select('*')
        .maybeSingle();
      data = inserted.data;
      error = inserted.error;
    }
  }

  if (error) {
    throwIfPettyCashMissing(error);
    throw error;
  }

  if (!data?.id) {
    // Confirm write by re-fetch (some RLS setups return no row from upsert select)
    const confirmed = await fetchPettyCashReport(day, gid);
    if (!confirmed?.id && !(confirmed?.rows || []).length && !(Number(confirmed?.openingBalance) > 0)) {
      throw new Error('Save did not persist. Check Supabase petty_cash_reports table / RLS.');
    }
    invalidateCache(`gym:${gid}:`);
    return confirmed;
  }

  invalidateCache(`gym:${gid}:`);
  return mapPettyCashReportRow(data, day);
}

function numOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function emptyZManualBlocks() {
  return {
    totalSaleSummary: {
      new_membership: '',
      renewal: '',
      pt_sale: '',
      guest_pass: '',
      locker_sale: '',
      spa_sale: '',
      oxybar: '',
      zumba_sale: '',
      yoga_sale: '',
      gym_apparel: '',
      miscellaneous_sale: '',
      remaining_clear: '',
    },
    totalExpenseSummary: {
      gym_cash: '',
      gym_bank: '',
      pearl_cash: '',
      pearl_bank: '',
    },
    totalSaleMethods: {
      cash: '',
      card: '',
      bank: '',
      qr: '',
      remaining_balance: '',
    },
    totalActiveMembers: {
      membership_sale_month: '',
      pt_sale_month: '',
      active_men: '',
      active_women: '',
    },
  };
}

function parseJsonbObject(value) {
  if (value == null) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  return null;
}

function mapZReportRow(data, day) {
  const blanks = emptyZManualBlocks();
  if (!data) {
    return {
      id: null,
      reportDate: day,
      openingCash: 0,
      cashReceived: 0,
      openingBank: 0,
      bankReceived: 0,
      bankSale: 0,
      closingCash: 0,
      closingBank: 0,
      pearlGroupCash: 0,
      pearlGroupBank: 0,
      bankAccounts: null,
      ...blanks,
      notes: '',
    };
  }
  const expenseRaw = { ...(data.total_expense_summary || {}) };
  // Cash/Bank SUMMARY pearl ONLY from summary_* keys (never fall back to expense pearl_*)
  const pearlGroupCash = numOrZero(
    data.pearl_group_cash ?? expenseRaw.summary_pearl_cash,
  );
  const pearlGroupBank = numOrZero(
    data.pearl_group_bank ?? expenseRaw.summary_pearl_bank,
  );
  const bankAccounts = parseJsonbObject(data.bank_accounts);
  return {
    id: data.id || null,
    reportDate: String(data.report_date || day || '').slice(0, 10),
    openingCash: numOrZero(data.opening_cash),
    cashReceived: numOrZero(data.cash_received),
    openingBank: numOrZero(data.opening_bank),
    bankReceived: numOrZero(data.bank_received),
    bankSale: numOrZero(data.bank_sale),
    closingCash: numOrZero(data.closing_cash ?? expenseRaw.closing_cash),
    closingBank: numOrZero(data.closing_bank ?? expenseRaw.closing_bank),
    pearlGroupCash,
    pearlGroupBank,
    bankAccounts,
    totalSaleSummary: { ...blanks.totalSaleSummary, ...(data.total_sale_summary || {}) },
    totalExpenseSummary: {
      gym_cash: expenseRaw.gym_cash ?? '',
      gym_bank: expenseRaw.gym_bank ?? '',
      // Expense-summary pearl only — ignore summary_pearl_* here
      pearl_cash: expenseRaw.pearl_cash ?? '',
      pearl_bank: expenseRaw.pearl_bank ?? '',
    },
    totalSaleMethods: { ...blanks.totalSaleMethods, ...(data.total_sale_methods || {}) },
    totalActiveMembers: { ...blanks.totalActiveMembers, ...(data.total_active_members || {}) },
    notes: String(data.notes || ''),
  };
}

/** Load manual Z-report fields for a gym/date. */
export async function fetchDailyZReport(reportDate, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(reportDate || localDateKey()).slice(0, 10);
  if (!gid || !day) return mapZReportRow(null, day);

  const { data, error } = await supabase
    .from('z_reports')
    .select('*')
    .eq('gym_id', gid)
    .eq('report_date', day)
    .maybeSingle();

  if (error) {
    if (/z_reports.*schema cache|relation.*z_reports.*does not exist/i.test(error.message || '')) {
      throw new Error(
        'Z Report table not found. Open Supabase → SQL Editor, run supabase/z_reports.sql, then refresh.',
      );
    }
    if (/does not exist|schema cache/i.test(error.message || '')) {
      return mapZReportRow(null, day);
    }
    throw error;
  }

  return mapZReportRow(data, day);
}

/** Save manual Z-report fields for a gym/date (update-or-insert; never touches z_report_v1). */
export async function saveDailyZReport(payload = {}, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(payload.reportDate || localDateKey()).slice(0, 10);
  if (!gid) throw new Error('No gym is linked to this account.');
  if (!day) throw new Error('Report date is required.');

  const blanks = emptyZManualBlocks();
  // Expense pearl (pearl_cash/pearl_bank) and summary pearl (summary_pearl_*) stay separate
  const closingCash = Math.round(Number(payload.closingCash) || 0);
  const closingBank = Math.round(Number(payload.closingBank) || 0);
  const expenseSummary = {
    gym_cash: payload.totalExpenseSummary?.gym_cash ?? '',
    gym_bank: payload.totalExpenseSummary?.gym_bank ?? '',
    pearl_cash: payload.totalExpenseSummary?.pearl_cash ?? '',
    pearl_bank: payload.totalExpenseSummary?.pearl_bank ?? '',
    summary_pearl_cash: Math.max(0, numOrZero(payload.pearlGroupCash)),
    summary_pearl_bank: Math.max(0, numOrZero(payload.pearlGroupBank)),
    // Fallback when closing_* columns not migrated yet
    closing_cash: closingCash,
    closing_bank: closingBank,
  };

  const bankClosings = payload.closingBanks
    || (payload.bankAccounts ? computeZBankClosings(payload.bankAccounts).closingBanks : null);
  const bankAccountsJson = payload.bankAccounts
    ? buildBankAccountsSavePayload(payload.bankAccounts, bankClosings || {})
    : null;
  const openingBank = bankAccountsJson
    ? sumZBankField(payload.bankAccounts, 'opening')
    : Math.max(0, numOrZero(payload.openingBank));
  const bankReceived = bankAccountsJson
    ? sumZBankField(payload.bankAccounts, 'received')
    : Math.max(0, numOrZero(payload.bankReceived));
  const bankSale = bankAccountsJson
    ? sumZBankField(payload.bankAccounts, 'sale')
    : Math.max(0, numOrZero(payload.bankSale));

  const fields = {
    opening_cash: Math.max(0, numOrZero(payload.openingCash)),
    cash_received: Math.max(0, numOrZero(payload.cashReceived)),
    opening_bank: Math.max(0, Math.round(Number(openingBank) || 0)),
    bank_received: Math.max(0, Math.round(Number(bankReceived) || 0)),
    bank_sale: Math.max(0, Math.round(Number(bankSale) || 0)),
    closing_cash: closingCash,
    closing_bank: closingBank,
    total_sale_summary: { ...blanks.totalSaleSummary, ...(payload.totalSaleSummary || {}) },
    total_expense_summary: expenseSummary,
    total_sale_methods: { ...blanks.totalSaleMethods, ...(payload.totalSaleMethods || {}) },
    total_active_members: { ...blanks.totalActiveMembers, ...(payload.totalActiveMembers || {}) },
    notes: String(payload.notes || ''),
    updated_at: new Date().toISOString(),
  };
  if (bankAccountsJson) {
    fields.bank_accounts = bankAccountsJson;
  }

  const { data: existing, error: fetchErr } = await supabase
    .from('z_reports')
    .select('id')
    .eq('gym_id', gid)
    .eq('report_date', day)
    .maybeSingle();

  if (fetchErr && !/does not exist|schema cache/i.test(fetchErr.message || '')) {
    if (/z_reports.*schema cache|relation.*z_reports.*does not exist/i.test(fetchErr.message || '')) {
      throw new Error(
        'Z Report table not found. Open Supabase → SQL Editor, run supabase/z_reports.sql, then refresh.',
      );
    }
    throw fetchErr;
  }

  const writeRow = async (body, { isInsert = false } = {}) => {
    if (isInsert) {
      return supabase
        .from('z_reports')
        .insert({ gym_id: gid, report_date: day, ...body })
        .select('*')
        .maybeSingle();
    }
    return supabase
      .from('z_reports')
      .update(body)
      .eq('id', existing.id)
      .select('*')
      .maybeSingle();
  };

  let { data, error } = await writeRow(fields, { isInsert: !existing?.id });

  // Older DBs without closing_* / bank_accounts — retry without unsupported columns
  if (error && /closing_cash|closing_bank|bank_accounts/i.test(error.message || '')) {
    const {
      closing_cash,
      closing_bank,
      bank_accounts,
      ...withoutExtra
    } = fields;
    void closing_cash;
    void closing_bank;
    void bank_accounts;
    ({ data, error } = await writeRow(withoutExtra, { isInsert: !existing?.id }));
  }

  if (error) {
    if (/bank_accounts/i.test(error.message || '')) {
      throw new Error(
        'Bank accounts column missing. Run supabase/z_report_bank_accounts.sql in Supabase SQL Editor, then refresh.',
      );
    }
    if (/z_reports.*schema cache|relation.*z_reports.*does not exist/i.test(error.message || '')) {
      throw new Error(
        'Z Report table not found. Open Supabase → SQL Editor, run supabase/z_reports.sql, then refresh.',
      );
    }
    throw error;
  }

  if (!data?.id) {
    throw new Error('Z Report save did not persist. Refresh and try again.');
  }

  invalidateCache(`gym:${gid}:`);
  return mapZReportRow(data, day);
}

function mapZReport1Row(data, day) {
  if (!data) {
    return {
      id: null,
      reportDate: day,
      zReportV1: null,
      hasV1: false,
    };
  }
  const payload = parseJsonbObject(data.z_report_v1);
  return {
    id: data.id || null,
    reportDate: String(data.report_date || day || '').slice(0, 10),
    zReportV1: payload,
    hasV1: Boolean(payload && Object.keys(payload).length > 0),
  };
}

/** Load Z Report 1 (Excel layout) JSON for a gym/date — does not touch classic Z fields. */
export async function fetchDailyZReport1(reportDate, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(reportDate || localDateKey()).slice(0, 10);
  if (!gid || !day) return mapZReport1Row(null, day);

  const { data, error } = await supabase
    .from('z_reports')
    .select('id, report_date, z_report_v1')
    .eq('gym_id', gid)
    .eq('report_date', day)
    .maybeSingle();

  if (error) {
    if (/z_report_v1/i.test(error.message || '')) {
      throw new Error(
        'Z Report 1 column missing. Run supabase/z_report_v1.sql in Supabase SQL Editor, then refresh.',
      );
    }
    if (/z_reports.*schema cache|relation.*z_reports.*does not exist/i.test(error.message || '')) {
      throw new Error(
        'Z Report table not found. Open Supabase → SQL Editor, run supabase/z_reports.sql, then refresh.',
      );
    }
    if (/does not exist|schema cache/i.test(error.message || '')) {
      return mapZReport1Row(null, day);
    }
    throw error;
  }

  return mapZReport1Row(data, day);
}

/** Upsert only z_report_v1 — classic Z Report columns stay unchanged. */
export async function saveDailyZReport1(payload = {}, explicitGymId) {
  const gid = await resolveGymId(explicitGymId);
  const day = String(payload.reportDate || localDateKey()).slice(0, 10);
  if (!gid) throw new Error('No gym is linked to this account.');
  if (!day) throw new Error('Report date is required.');

  const v1 = payload.zReportV1 && typeof payload.zReportV1 === 'object'
    ? payload.zReportV1
    : null;
  if (!v1 || Object.keys(v1).length === 0) {
    throw new Error('Z Report 1 payload is empty — nothing to save.');
  }
  const updatedAt = new Date().toISOString();

  const { data: existing, error: fetchErr } = await supabase
    .from('z_reports')
    .select('id')
    .eq('gym_id', gid)
    .eq('report_date', day)
    .maybeSingle();

  if (fetchErr && !/does not exist|schema cache/i.test(fetchErr.message || '')) {
    if (/z_reports.*schema cache|relation.*z_reports.*does not exist/i.test(fetchErr.message || '')) {
      throw new Error(
        'Z Report table not found. Open Supabase → SQL Editor, run supabase/z_reports.sql, then refresh.',
      );
    }
    throw fetchErr;
  }

  let data;
  let error;

  if (existing?.id) {
    ({ data, error } = await supabase
      .from('z_reports')
      .update({ z_report_v1: v1, updated_at: updatedAt })
      .eq('id', existing.id)
      .select('id, report_date, z_report_v1')
      .maybeSingle());
  } else {
    ({ data, error } = await supabase
      .from('z_reports')
      .insert({
        gym_id: gid,
        report_date: day,
        z_report_v1: v1,
        updated_at: updatedAt,
      })
      .select('id, report_date, z_report_v1')
      .maybeSingle());
  }

  if (error) {
    if (/z_report_v1/i.test(error.message || '')) {
      throw new Error(
        'Z Report 1 column missing. Run supabase/z_report_v1.sql in Supabase SQL Editor, then refresh.',
      );
    }
    if (/z_reports.*schema cache|relation.*z_reports.*does not exist/i.test(error.message || '')) {
      throw new Error(
        'Z Report table not found. Open Supabase → SQL Editor, run supabase/z_reports.sql, then refresh.',
      );
    }
    throw error;
  }

  let mapped = mapZReport1Row(data, day);
  if (!mapped.hasV1) {
    // RLS / empty RETURNING can fake success — re-read and fail loudly if not persisted
    mapped = await fetchDailyZReport1(day, gid);
  }
  if (!mapped.hasV1) {
    throw new Error(
      'Z Report 1 save did not persist. Confirm z_report_v1 column exists, then try Save again.',
    );
  }

  invalidateCache(`gym:${gid}:`);
  return mapped;
}

export { CHART_OF_ACCOUNTS, EXPENSE_CATEGORIES, ACCOUNT_TYPES, suggestNextAccountCode, getChartOfAccountsForGym, COA_PARENT_ACCOUNTS };
